import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import withClientId from '@/hocs/with-client-id';
import { withRouter, Link } from 'react-router-dom';
import {
	Typography,
	Icon,
	Popover,
	Button,
	Skeleton,
	Alert,
	Switch,
	Radio,
} from '@/elements';
import Animate from 'rc-animate';

import './index.scss';
import * as ROUTES from '@/config/routes';
import * as actions from '@/redux/new_store_structure/actions';
import { getActionTypeMsg, getTargetTypeMsg } from '../helper';
import { TESTING_CLIENT_IDS } from '@/config/constants';

const { Title, Paragraph, Text } = Typography;
const RadioGroup = Radio.Group;

const NotificationsList = props => {
	const {
		clientId,
		getList,
		list,
		delNotification,
		loading,
		updateStatus,
		verticalConfig,
	} = props;
	const evacDemoEnabled = TESTING_CLIENT_IDS.includes(clientId);
	const enabledVerticals = Object.keys(verticalConfig);
	// state
	const [type, setType] = useState(evacDemoEnabled ? 'all' : 'core');
	// handlers
	function handleType(e) {
		setType(e.target.value);
	}
	// effects
	useEffect(() => {
		getList({ clientId });
	}, []);
	function onChangeSwitch(value, item) {
		updateStatus({ ...item, active: value }, 'edit');
	}
	// addition elements
	function toDel(id) {
		return (
			<div className="btnWrap">
				<Button
					className={'delete'}
					type="danger"
					onClick={() => delNotification({ clientId, alertId: id })}
				>
					yes
				</Button>
				<Button>no</Button>
			</div>
		);
	}
	const emptyMsg = type => {
		switch (type) {
			case 'thermal':
				return 'Thermal';
			case 'evacuation':
				return 'Evacuation';
			case 'core':
				return 'General';
			default:
				return '';
		}
	};
	const filteredList =
		list &&
		list.length > 0 &&
		list
			.filter(el => {
				if (type === 'all') return true;
				return el.type === type;
			})
			.filter(el => enabledVerticals.includes(el.type));
	//effect for ellipsis text on content msg
	const [isExpand, setExpand] = useState([]);
	useEffect(() => setExpand(Array(filteredList.length).fill(false)), [
		filteredList.length,
	]);
	function handleExpand(i) {
		const newExpand = isExpand.map((el, ind) => i === ind);
		setExpand(newExpand);
	}
	return (
		<div id="notifications-list">
			<Title>
				Notifications
				{` `}
				<Link to={`${ROUTES.NOTIFICATIONS}/add/new`}>
					<Icon type="plus-circle" />
				</Link>
			</Title>
			<Paragraph>
				Create or edit your notifications. These are messages that are
				sent automatically based on criteria you select.
			</Paragraph>
			{evacDemoEnabled && (
				<div className={'type-wrap'}>
					<RadioGroup value={type} onChange={handleType}>
						<Radio.Button value="all">All</Radio.Button>
						<Radio.Button value="core">General</Radio.Button>
						{enabledVerticals.includes('evacuation') && (
							<Radio.Button value="evacuation">
								Evacuation
							</Radio.Button>
						)}
						{enabledVerticals.includes('thermal') && (
							<Radio.Button value="thermal">Thermal</Radio.Button>
						)}
					</RadioGroup>
				</div>
			)}
			<div className="list-wrap">
				{loading ? (
					<div className="list-item-wrap">
						<div className="head" />
						<div className="content-wrap">
							<Skeleton active />
						</div>
					</div>
				) : (
					<Animate transitionName="fade" transitionAppear>
						{filteredList && filteredList.length > 0 ? (
							filteredList.map((item, i) => (
								<div
									key={item.alertId}
									className="list-item-wrap"
								>
									<div className="head">
										<Title
											ellipsis
											level={4}
											onClick={() =>
												props.history.push(
													`${ROUTES.NOTIFICATIONS}/edit/${item.alertId}`,
												)
											}
										>
											{item.alertName}
										</Title>
										<div
											className="stopPropagation"
											onClick={e => e.stopPropagation()}
										>
											<Text
												style={{
													padding: '0 10px',
												}}
											>
												{item.type === 'core' &&
													'General'}
												{item.type === 'evacuation' &&
													'Evacuation'}
												{item.type === 'thermal' &&
													'Thermal'}
											</Text>
											<Switch
												checked={item.active}
												onChange={val =>
													onChangeSwitch(val, item)
												}
											/>
											<Popover
												content={toDel(item.alertId)}
												title="Delete ?"
												trigger="click"
												getPopupContainer={() =>
													document.getElementById(
														'notifications-list',
													)
												}
											>
												<Icon type="delete" />
											</Popover>
										</div>
									</div>
									<div className="content-wrap">
										<Paragraph
											ellipsis={{
												rows: 1,
												expandable: true,
												onExpand: () => handleExpand(i),
											}}
											className={`content ${
												isExpand[i] ? 'expand' : ''
											}`}
										>
											{item.alertTemplate}
										</Paragraph>
										<div className="footer">
											<div className="footer-item">
												<Text strong>When</Text>
												<Text>
													{getActionTypeMsg(
														item.actionType,
													)}
												</Text>
											</div>
											<div className="footer-item">
												<Text strong>Who</Text>
												<Text>
													{getTargetTypeMsg(
														item.targetType,
													)}
												</Text>
											</div>
											<div className="footer-item">
												<Text strong>Zones</Text>
												<Paragraph
													ellipsis={{ rows: 3 }}
												>
													{item.zoneIds
														? item.zoneIds.join(
																', ',
														  )
														: '- -'}
												</Paragraph>
											</div>
											<div className="footer-item">
												<Text strong>Frequency</Text>
												<Text>
													{item.frequency === 0
														? `Once`
														: item.frequency ===
														  1440
														? 'Once a day'
														: `Every ${item.frequency} mins`}
												</Text>
											</div>
											<div className="footer-item">
												<Text strong>Message type</Text>
												<Text
													style={{
														textTransform:
															'capitalize',
													}}
												>
													{item.messageType === 'sms'
														? item.messageType.toUpperCase()
														: item.messageType}
												</Text>
											</div>
										</div>
									</div>
								</div>
							))
						) : (
							<Alert
								type="info"
								showIcon
								message={
									<div>
										{`You don’t have any ${emptyMsg(
											type,
										)} notifications yet. Click the `}
										<Icon type="plus-circle" />
										{` button above to get started.`}
									</div>
								}
							/>
						)}
					</Animate>
				)}
			</div>
		</div>
	);
};

const mapStateToProps = ({
	notifications,
	user: {
		currentUser: { verticalConfig },
	},
}) => {
	return {
		list: notifications.list,
		loading: notifications.loading,
		verticalConfig,
	};
};

const mapDispatchToProps = dispatch => {
	return {
		getList: params => dispatch(actions.getNotificationsList(params)),
		delNotification: params => dispatch(actions.delNotification(params)),
		updateStatus: (params, effect) =>
			dispatch(actions.addEditNotification(params, effect)),
	};
};

export default compose(
	withRouter,
	withClientId,
	connect(mapStateToProps, mapDispatchToProps),
)(NotificationsList);



// WEBPACK FOOTER //
// ./src/pages/notifications/notifications-list/index.js